// src/components/Layout.tsx
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import { Outlet, useOutletContext, useNavigate, useLocation } from "react-router-dom";
import SideNav from "./SideNav";
import React, { Suspense, useState, useEffect, useRef } from "react";
import { PanelLeft, Settings, Store, FileText, Sparkles, MessageSquareText, CreditCard, GraduationCap, LogOut, MessageSquare, StickyNote, Code, Building2, ShieldCheck } from "lucide-react";
import DraggableChatWrapper from "./DraggableChatWrapper";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "@apollo/client/react";
import { USER_QUERY } from "../graphql/queries";
import Divider from "@mui/material/Divider";
import { isPartnerCompanySwitchEnabled, isPartnerMultiTenantRole, shouldOpenPartnerCompanyPicker } from "../utils/partnerRoute";
import { TOGGLE_FACILITY_CHAT } from "../graphql/constants";
import { canAccessDeveloperConsole } from "../utils/developerConsoleAccess";
import DocumentExtractionBell from "./DocumentExtractionBell";
import ExtractionReviewAutoOpen from "./ExtractionReviewAutoOpen";
import { useAiEnabled } from "../hooks/useAiEnabled";
import { useRouteSuspenseFallback } from "./skeletons/routeSuspenseFallback";
import { getRoutePageTitle } from "../utils/routePageTitles";
import { prefetchLocationMap } from "../pages/locationMap/prefetchLocationMap";

const IncidentAssistantChatbot = React.lazy(() => import("./IncidentAssistantChatbot"));

type LayoutOutletContext = {
  setHeaderTitle: (title: string) => void;
  onMobileClose?: () => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
};

const Layout = () => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const suspenseFallback = useRouteSuspenseFallback();
  const isSmallScreen = useMediaQuery("(max-width:772px)");
  const { user, logout, setUser } = useAuth();
  const aiEnabled = useAiEnabled();
  const { data: meData } = useQuery<{ me?: { aiEnabled?: boolean } }>(USER_QUERY, {
    skip: !user,
    fetchPolicy: "network-only",
    context: { skipLoader: true },
  });

  useEffect(() => {
    if (!user || !meData?.me) return;
    if (meData.me.aiEnabled !== user.aiEnabled) {
      setUser({ ...user, aiEnabled: meData.me.aiEnabled });
    }
  }, [meData, setUser, user]);
  const navigate = useNavigate();
  const location = useLocation();
  const partnerCompanySwitchEnabled = isPartnerCompanySwitchEnabled();

  useEffect(() => {
    const title = getRoutePageTitle(location.pathname);
    if (title) setHeaderTitle(title);
  }, [location.pathname]);

  useEffect(() => {
    const warm = () => prefetchLocationMap();
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(warm, { timeout: 2500 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(warm, 1200);
    return () => window.clearTimeout(t);
  }, []);
  // On mobile, start with panels closed. On desktop, start with sidebar open
  const [sidenavOpen, setSidenavOpen] = useState<boolean>(true);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true); // Start with main menu collapsed on desktop
  const [headerTitle, setHeaderTitle] = useState<string>("My Application");
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [isIncidentChatOpen, setIsIncidentChatOpen] = useState<boolean>(false);

  // Update state when breakpoint changes
  useEffect(() => {
    if (isSmallScreen) {
      // On small screens, keep icon-only sidebar visible by default
      setSidenavOpen(true);
      setIsCollapsed(true);
    } else {
      setSidenavOpen(true);
    }
  }, [isSmallScreen]);

  // Guard partner users from accessing tenant pages before selecting a tenant company.
  // Keep /select-company always accessible so they can switch companies anytime.
  useEffect(() => {
    if (!user) return;
    const onSelectCompanyPage = location.pathname === "/select-company";
    if (partnerCompanySwitchEnabled && shouldOpenPartnerCompanyPicker(user) && !onSelectCompanyPage) {
      navigate("/select-company", { replace: true });
    }
  }, [user, location.pathname, navigate, partnerCompanySwitchEnabled]);

  const handleMobileClose = () => {
    if (isSmallScreen) {
      // Close overlay back to icon-only sidebar on small screens
      setIsCollapsed(true);
      setSidenavOpen(true);
    }
  };

  const handleToggleSidebar = () => {
    if (isSmallScreen) {
      // On small screens, toggle between icon-only rail and expanded overlay
      if (isCollapsed) {
        setIsCollapsed(false);
        setSidenavOpen(true);
      } else {
        setIsCollapsed(true);
        setSidenavOpen(true);
      }
      return;
    }

    // Desktop/tablet behavior: toggle between collapsed and expanded
    setIsCollapsed(!isCollapsed);
    if (!sidenavOpen) {
      setSidenavOpen(true);
    }
  };

  // Get first letter of first name for avatar
  const getFirstLetter = () => {
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user?.userdisplayname || user?.name) {
      const name = (user.userdisplayname || user.name || "").trim();
      return name.charAt(0).toUpperCase() || "?";
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "?";
  };

  // Get full name for display
  const getFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.userdisplayname || user?.name || "User";
  };

  // Get user email
  const getUserEmail = () => {
    return user?.email || "";
  };

  // Common handler for MenuItem onClick
  const handleMenuItemClick = (route?: string | null) => {
    setUserMenuAnchor(null);
    if (route === null) {
      // Logout action
      logout();
    } else if (route) {
      // Navigation action
      navigate(route);
    }
  };

  // Menu items configuration
  const menuItems = [
    {
      icon: Settings,
      label: "Settings",
      route: "/settings",
      dividerBefore: false,
    },
    {
      icon: Store,
      label: "Vendor",
      route: "/vendor",
      dividerBefore: false,
    },
    {
      icon: FileText,
      label: "Master Data Request",
      route: "/masterdatarequest",
      dividerBefore: false,
    },
    {
      icon: Sparkles,
      label: "AI Marketplace",
      route: "/ai-marketplace",
      dividerBefore: false,
    },
    {
      icon: Code,
      label: "Developer Console",
      route: "/developer-console",
      dividerBefore: false,
    },
    {
      icon: CreditCard,
      label: "Plan & Billing",
      route: "/subscription",
      dividerBefore: false,
    },
    {
      icon: GraduationCap,
      label: "Onboarding Sessions",
      route: "/onboardingsessions",
      dividerBefore: false,
    },
    {
      icon: StickyNote,
      label: "Company Notes",
      route: "/company-notes",
      dividerBefore: false,
    },
    {
      icon: ShieldCheck,
      label: "Compliance Settings",
      route: "/compliance-settings",
      dividerBefore: false,
    },
    // {
    //   icon: Building2,
    //   label: "Switch Company",
    //   route: "/select-company",
    //   dividerBefore: true,
    //   partnerOnly: true,
    // },
    {
      icon: LogOut,
      label: "Logout",
      route: null,
      dividerBefore: true,
      partnerOnly: false,
    },
  ];

  const filteredMenuItems = menuItems.filter(
    (item) =>
      !("partnerOnly" in item && item.partnerOnly && !isPartnerMultiTenantRole(user?.role)) &&
      (item.route !== "/developer-console" || canAccessDeveloperConsole(user?.role))
  );

  const userJwtToken = localStorage.getItem("accessToken");
  return (
    <Box sx={{ position: "relative", height: "100vh", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", height: "100vh" }}>
        <SideNav
          isOpen={sidenavOpen}
          setIsOpen={setSidenavOpen}
          onMobileClose={handleMobileClose}
          isCollapsed={isCollapsed}
          onCollapseChange={setIsCollapsed}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0, // Allows flexbox to shrink below content size
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            component="header"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#fff",
              gap: 2,
              boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1)",
              p: 2,
              position: "sticky",
              top: 0,
              zIndex: 99,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                size="small"
                aria-label={
                  isSmallScreen
                    ? (isCollapsed ? "Expand Sidebar" : "Collapse Sidebar")
                    : (isCollapsed ? "Expand Sidebar" : "Collapse Sidebar")
                }
                onClick={handleToggleSidebar}
                sx={{ color: "#6b7280" }}
              >
                <PanelLeft size={16} />
              </IconButton>
              <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 700 }}>
                {headerTitle}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
              }}
            >
              {partnerCompanySwitchEnabled && isPartnerMultiTenantRole(user?.role) && (
                <Tooltip title="Switch Company" placement="bottom" arrow>
                  <IconButton
                    onClick={() => navigate("/select-company")}
                    sx={{
                      height: 34,
                      width: 34,
                      color: "#2563eb",
                      bgcolor: "#eff6ff",
                      transition: "background-color 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        bgcolor: "#dbeafe",
                        transform: "scale(1.05)",
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                  >
                    <Building2 size={18} />
                  </IconButton>
                </Tooltip>
              )}
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  overflow: "visible",
                  "@keyframes circleWave": {
                    "0%": { transform: "scale(1)", opacity: 0.7 },
                    "100%": { transform: "scale(1.8)", opacity: 0 },
                  },
                  "&::before, &::after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: 40,
                    height: 40,
                    marginTop: -20,
                    marginLeft: -20,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: "secondary.main",
                    animation: "circleWave 2s ease-out infinite",
                    pointerEvents: "none",
                    boxSizing: "border-box",
                  },
                  "&::after": {
                    animationDelay: "1s",
                  },
                }}
              >
                <IconButton
                  onClick={() => {
                    if (!aiEnabled || !location.pathname.startsWith('/locationmap')) {
                      setIsIncidentChatOpen(!isIncidentChatOpen);
                      return;
                    }
                    window.dispatchEvent(new CustomEvent(TOGGLE_FACILITY_CHAT));
                  }}
                  sx={{
                    position: "relative",
                    height: 34,
                    width: 34,
                    zIndex: 1,
                    color: isIncidentChatOpen ? "secondary.contrastText" : "secondary.contrastText",
                    bgcolor: "secondary.main",
                    transition: "background-color 0.2s ease, color 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      bgcolor: "secondary.dark",
                      transform: "scale(1.05)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  {/* <Sparkles size={20} /> */}
                  <MessageSquareText size={20} />
                </IconButton>
              </Box>
              {user && <ExtractionReviewAutoOpen />}
              {user && <DocumentExtractionBell />}
              <Box
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  p: "4px 8px",
                  borderRadius: "20px",
                }}
              >
                <Avatar
                  className="avatar-icon"
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: 14,
                    backgroundColor: "primary.main",
                    color: "#fff",
                    transition: "background-color 0.2s",
                  }}
                >
                  {getFirstLetter()}
                </Avatar>
                {/* {!isSmallScreen && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#374151",
                      userSelect: "none",
                    }}
                  >
                    {getFullName()}
                  </Typography>
                )} */}
              </Box>



            </Box>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  bgcolor: "#fff",
                  minWidth: 280,
                  borderRadius: "12px",
                  boxShadow: "0 0 6px #7faee052",
                  mt: 1,
                  py: 0,
                },
              }}
            >
              {/* User Info Section */}
              <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 14,
                    backgroundColor: "primary.main",
                    color: "#fff",
                  }}
                >
                  {getFirstLetter()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      mb: 0.25,
                    }}
                  >
                    {getFullName()}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getUserEmail()}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 0.5 }} />

              {/* Menu Items */}
              {filteredMenuItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <React.Fragment key={item.route ?? item.label}>
                    {item.dividerBefore && <Divider sx={{ my: 0.5 }} />}
                    <MenuItem
                      onClick={() => handleMenuItemClick(item.route)}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#6b7280",
                        py: 1,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": {
                          backgroundColor: "#f3f4f6",
                        },
                      }}
                    >
                      <IconComponent size={16} style={{ color: "#6b7280" }} />
                      <span>{item.label}</span>
                    </MenuItem>
                  </React.Fragment>
                );
              })}
            </Menu>
          </Box>
          <Box
            ref={scrollContainerRef}
            className="p-3"
            sx={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <Suspense fallback={suspenseFallback}>
              <Outlet
                context={{
                  setHeaderTitle,
                  onMobileClose: handleMobileClose,
                  scrollContainerRef,
                }}
              />
            </Suspense>
          </Box>
        </Box>
      </Box>
      {/* <div className="asset-chat-ui">
      <BubbleChat
        chatflowid="599a35f8-ed63-434b-ab6a-3544b263df23"
        apiHost="https://chatbot.criticalasset.com"
        theme={bubbleTheme}
        chatflowConfig={{ vars: { jwt: userJwtToken } }}
      />
    </div> */}
      {isIncidentChatOpen && (
        <DraggableChatWrapper isOpen={isIncidentChatOpen}>
          <Suspense fallback={null}>
            <IncidentAssistantChatbot onClose={() => setIsIncidentChatOpen(false)} />
          </Suspense>
        </DraggableChatWrapper>
      )}
    </Box>
  );
};

export default Layout;

export const useLayoutContext = () => useOutletContext<LayoutOutletContext>();
